import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ButtonBase, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import { PVE_ROLE_SPEC_FLAGS, PVE_SPEC_FLAG_LABELS, PveSpecFlag, specFlags, toggleSpecFlag } from '@pvplevelup/core/dist/pve/PveRoleSpecs';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins

/** the specialized slot flags (PVE_PLAN.md D25) */
export const SPEC_FLAG_COLORS: Record<PveSpecFlag, string> = {
  TankAA: '#90caf9',
  HealerAA: '#aed581',
  RogueAA: '#ff8a65',
  MageAA: '#b39ddb',
  Kiter: '#4dd0e1',
};
const TIPS: Record<PveSpecFlag, string> = {
  TankAA: 'Tank AA: a tank slot for the Tank AA',
  HealerAA: 'Healer AA: a healer slot for the Healer AA, which some dungeons need to complete',
  RogueAA: 'Rogue AA: a DPS slot for the Rogue AA',
  MageAA: 'Mage AA: a DPS slot for the Mage AA',
  Kiter: 'Kiter: a DPS slot for someone who kites',
};

const useStyles = makeStyles(() => ({
  tags: {
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridColumnGap: 4,
    alignItems: 'center',
    verticalAlign: 'middle',
  },
  tag: {
    display: 'inline-grid',
    placeItems: 'center',
    height: 18,
    padding: '0 6px',
    borderRadius: 5,
    fontSize: '0.58rem',
    fontWeight: 900,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  toggle: {
    transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
    '&:hover': { filter: 'brightness(1.2)' },
  },
  off: {
    color: 'rgba(255, 255, 255, 0.3)',
    background: 'transparent',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.13)',
    '&:hover': { color: 'rgba(255, 255, 255, 0.7)', boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.3)' },
  },
}));

const onStyle = (flag: PveSpecFlag) => ({
  color: SPEC_FLAG_COLORS[flag],
  background: `${SPEC_FLAG_COLORS[flag]}24`,
  boxShadow: `inset 0 0 0 1px ${SPEC_FLAG_COLORS[flag]}66`,
});

interface Props {
  role: string | null | undefined;
  spec: string | null | undefined;
  /** the group leader toggles each flag (all of the role's flags show, the unset ones dimmed) */
  onChange?: (spec: string) => void;
  disabled?: boolean;
  className?: string;
}

/** Tank AA / Healer AA / Rogue AA / Mage AA / Kiter tags for a slot or player; toggles for the group leader */
export const RoleSpecTags: React.FC<Props> = ({role, spec, onChange, disabled, className}) => {
  const classes = useStyles();
  const flags = (role && PVE_ROLE_SPEC_FLAGS[role]) || [];
  const set = specFlags(spec, role);
  if(!onChange){
    if(set.length == 0)
      return null;
    return (
      <span className={clsx(classes.tags, className)}>
        {set.map((flag) => (
          <Tooltip key={flag} title={TIPS[flag]}>
            <span className={classes.tag} style={onStyle(flag)}>{PVE_SPEC_FLAG_LABELS[flag]}</span>
          </Tooltip>
        ))}
      </span>
    );
  }
  if(flags.length == 0)
    return null;
  return (
    <span className={clsx(classes.tags, className)}>
      {flags.map((flag) => {
        const on = set.indexOf(flag) != -1;
        return (
          <Tooltip key={flag} title={on ? `${TIPS[flag]}. Click to unmark.` : `Mark this slot ${PVE_SPEC_FLAG_LABELS[flag]}`}>
            <span>
              <ButtonBase className={clsx(classes.tag, classes.toggle, {[classes.off]: !on})} disabled={disabled} style={on ? onStyle(flag) : undefined}
                onClick={(e) => { e.stopPropagation(); onChange(toggleSpecFlag(spec, flag)); }}>
                {PVE_SPEC_FLAG_LABELS[flag]}
              </ButtonBase>
            </span>
          </Tooltip>
        );
      })}
    </span>
  );
}

export default RoleSpecTags;
